// src/pages/shiftlens/ShiftLens.jsx

import React, { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import LoadingScreen from 'components/loading_screen/LoadingScreen';
import Unauthorized from 'components/unauthorized/Unauthorized';
import AverageConsumptionByCustomer from 'components/graphs/AverageConsumptionByCustomer';
import TotalMileageByProductVersion from 'components/graphs/TotalMileageByProductVersion';
import VehiclePerformanceOverview from 'components/vehicle_performance/VehiclePerformanceOverview';
import SearchBar from 'components/search/SearchBar';
import DefectAnalytics from './DefectAnalytics';
import './ShiftLens.css';
import { BASE_URL } from 'BaseURL';
import Modal from 'react-modal';

Modal.setAppElement('#root');

const ShiftLens = () => {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [status, setStatus] = useState({ loading: true, error: null });
  const [isNewDefectAnalytics, setIsNewDefectAnalytics] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}shiftware/api/v1/vehicle-performance/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(response.data);
      } catch (err) {
        const errorMsg = err.response?.status === 403 ? 'Unauthorized' : 'Failed to fetch data';
        setStatus({ loading: false, error: errorMsg });
      } finally {
        setStatus((prev) => ({ ...prev, loading: false }));
      }
    };

    if (token) {
      fetchData();
    } else {
      setStatus({ loading: false, error: 'No token available' });
    }
  }, [token]);

  const filteredData = useMemo(() => {
    const lowercasedQuery = searchQuery.toLowerCase();
    return data.filter(
      (item) =>
        item.vehicle_name.toLowerCase().includes(lowercasedQuery) ||
        item.product_version.toLowerCase().includes(lowercasedQuery) ||
        item.customer_name.toLowerCase().includes(lowercasedQuery)
    );
  }, [searchQuery, data]);

  const columns = useMemo(
    () => [
      { Header: 'Vehicle Name', accessor: 'vehicle_name' },
      { Header: 'Customer Name', accessor: 'customer_name' },
      { Header: 'Product Version', accessor: 'product_version' },
      { Header: 'Days of Operation', accessor: 'days_of_operation' },
      { Header: 'Average Consumption', accessor: 'average_consumption' },
      { Header: 'Total Cumulative Mileage', accessor: 'total_cummulative_mileage' },
      { Header: 'Max Motor Temp', accessor: 'max_temp' },
    ],
    []
  );

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 'defectAnalytics') {
      setIsNewDefectAnalytics(false);
    }
  };

  if (status.loading) return <LoadingScreen />;
  if (status.error)
    return status.error === 'Unauthorized' ? <Unauthorized /> : <p>Error: {status.error}</p>;

  return (
    <div className="shiftlens-container">
      <header className="shiftlens-header">
        <h2>ShiftLens Dashboard</h2>
      </header>

      <nav className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => handleTabClick('overview')}
        >
          <span className="tab-icon">🚗</span>
          Vehicle Performance
        </button>
        <button
          className={`tab-button ${activeTab === 'defectAnalytics' ? 'active' : ''}`}
          onClick={() => handleTabClick('defectAnalytics')}
        >
          <span className="tab-icon">🔧</span>
          Defect Analytics
          {isNewDefectAnalytics && <span className="new-badge">NEW</span>}
        </button>
      </nav>

      <main className="tab-content">
        {activeTab === 'overview' && (
          <>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            <section className="charts-container">
              <TotalMileageByProductVersion data={filteredData} />
              <AverageConsumptionByCustomer data={filteredData} />
            </section>
            <VehiclePerformanceOverview data={filteredData} columns={columns} />
          </>
        )}

        {activeTab === 'defectAnalytics' && <DefectAnalytics />}
      </main>

      <footer className="shiftlens-footer">
        <div className="footer-content">
          <p>&copy; {new Date().getFullYear()} ShiftEV. All rights reserved.</p>
          <p>
            Contact us at <a href="mailto:data@shift-ev.com">data@shift-ev.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ShiftLens;
